<template>
  <div class="subcontractor-details-info">
    <div class="subcontractor-details-info__fields">
      <fields-renderer
        :config="fieldsRendererConfig"
        :form-data="formData"
        :fields-config="fieldsConfig"
        :validation-errors="validationErrors"
        :initial-autocompletes="initialAutocompletes"
        :permissions="permissions"
        :edit="edit"
        :create-mode="createMode"
        :update-data="updateData"
        :update-validation-errors="updateValidationErrors"
        :remove-data-property-by-key="removeDataPropertyByKey"
        :replace-property-value-by-key="replacePropertyValueByKey"
        @unlock-fields-on-dbl-click="unlockFieldsOnDblClick"
      />
    </div>

    <div class="subcontractor-details-info__map">
      <winter-service-map-panel
        :latitude="formData.latitude"
        :longitude="formData.longitude"
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import FieldsRenderer from '@/global/components/form-layout/FieldsRenderer.vue'
import WinterServiceMapPanel from '@/modules/road-maintenance-module/winter-service-v2/common/components/WinterServiceMapPanel.vue'

const props = defineProps({
  // The block's own config from the tab's `tabContent` - its single `rows` entry
  // holds the field list (see useSubcontractorDetails for why it lives there).
  config: {
    type: Object,
    default: () => ({})
  },
  formData: {
    type: Object,
    default: () => ({})
  },
  fieldsConfig: {
    type: Object,
    default: () => ({})
  },
  validationErrors: {
    type: Object,
    default: () => ({})
  },
  initialAutocompletes: {
    type: Object,
    default: () => ({})
  },
  permissions: {
    type: Object,
    default: null
  },
  edit: {
    type: Boolean,
    default: false
  },
  createMode: {
    type: Boolean,
    default: false
  },
  updateData: {
    type: Function,
    default: () => {}
  },
  updateValidationErrors: {
    type: Function,
    default: () => {}
  },
  removeDataPropertyByKey: {
    type: Function,
    default: () => {}
  },
  replacePropertyValueByKey: {
    type: Function,
    default: () => {}
  }
})

const emit = defineEmits(['set-edit'])

// FieldsRenderer only reports the double-click; turning the tab into edit mode is
// the block's job, and Content.vue is what listens for `set-edit`. FieldsRenderer
// has already checked the field itself - what is left is the tab-level guard.
const unlockFieldsOnDblClick = () => {
  if (props.createMode || (props.permissions && !props.permissions.canEdit)) {
    return
  }

  emit('set-edit', true)
}

// One field per row, so the form reads as the single column the design shows
// next to the map.
const fieldsRendererConfig = computed(() => {
  const [fieldsRow] = props.config?.rows || []

  return {
    columnsCount: fieldsRow?.columnsCount || 1,
    fields: fieldsRow?.fields || []
  }
})
</script>

<style scoped>
.subcontractor-details-info {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 24px;
  /* The map has its own height below - it should not stretch to match however
     tall the form ends up being. */
  align-items: start;
  width: 100%;
  box-sizing: border-box;
  padding: 16px 15px;
}

.subcontractor-details-info__fields {
  min-width: 0;
}

.subcontractor-details-info__map {
  min-width: 0;
  height: 560px;
  position: sticky;
  top: 16px;
}

@media (max-width: 960px) {
  .subcontractor-details-info {
    grid-template-columns: minmax(0, 1fr);
  }

  .subcontractor-details-info__map {
    height: 320px;
    position: static;
  }
}
</style>
